import { showToast } from "@/composables/swal";
import { http } from "@/interceptors/axiosRequestInterceptor";
import { OrderDetail } from "@/models";
import { useQueryClient } from "@tanstack/vue-query";
import { computed, reactive, ref } from "vue";
import {
    CustimooOrderFlowStatuses,
    activityStatus,
    getCurrentDatetime,
} from "./index";
import { useVueQuery } from "./useVueQuery";

export interface OrderItem {
    id: number;
    factory_id?: number;
    factory_name?: string;
    status: string;
    status_activities: any[];
    factory_products: any[];
}

export interface Order {
    id: number;
    order_no?: string;
    is_quote_order: boolean;
    admin_salesrep_id?: number;
    items: OrderItem[];
    customer: {
        first_name: string;
        last_name: string;
        email: string;
    };
    general_comments?: string;
    additional_fields?: {
        po_number?: string;
        is_manual_order?: boolean;
    };
    customer_reference_no?: string;
    quote_text?: string;
    design_file?: string;
}

export interface QuoteData {
    quote_text: string;
    quote_order_id: string | number;
    quote_order_no: string;
}

export interface OrderDetailState {
    is_quote_order: boolean;
    show_quote_buttons: boolean;
    show_provide_quote_button: boolean;
    no_custimo_order: boolean;
    order_factories: Array<{ id: number; name: string }>;
}

export const useOrderDetail = () => {
    const state = reactive<OrderDetailState>({
        is_quote_order: false,
        show_quote_buttons: false,
        show_provide_quote_button: false,
        no_custimo_order: false,
        order_factories: [],
    });

    // Quote-related state
    const quote = reactive<QuoteData>({
        quote_text: "",
        quote_order_id: "",
        quote_order_no: "",
    });

    const queryClient = useQueryClient();

    // Order detail query
    const getOrderDetail = (orderId: string | number) => {
        return useVueQuery(
            `order-detail-${orderId}`,
            async () => {
                if (!orderId) {
                    throw new Error("Order ID is required");
                }

                const url = `customer-orders/${orderId}`;
                const response = await http.get(url);
                const responseData = response.data;

                if (!responseData.success) {
                    throw new Error(
                        responseData.message || "Failed to fetch order details",
                    );
                }

                return responseData.result as OrderDetail;
            },
            {
                enabled: !!orderId,
                staleTime: 5 * 60 * 1000, // 5 minutes
                cacheTime: 10 * 60 * 1000, // 10 minutes
                retry: 3,
                retryDelay: (attemptIndex) =>
                    Math.min(1000 * 2 ** attemptIndex, 30000),
                onSuccess: (data: Order) => {
                    // Process order data
                    state.is_quote_order = data.is_quote_order;
                    state.order_factories = [];

                    // Process order items
                    data.items.forEach((orderItem) => {
                        if (orderItem.factory_id) {
                            state.order_factories.push({
                                id: orderItem.factory_id,
                                name: orderItem.factory_name,
                            });
                        }

                        // Process factory products
                        orderItem.factory_products.forEach((factoryProduct) => {
                            factoryProduct.custom_logos.forEach(
                                (customLogo) => {
                                    if (!("original_logo" in customLogo)) {
                                        customLogo.original_logo =
                                            customLogo.original_logo_url;
                                    }
                                },
                            );
                        });
                    });

                    // Handle quote order logic
                    if (state.is_quote_order) {
                        const lastOrderItem = data.items.at(-1);
                        if (lastOrderItem?.status === "quote_requested") {
                            if (data.admin_salesrep_id) {
                                state.show_quote_buttons = true;
                            } else {
                                state.show_provide_quote_button = true;
                            }
                        }
                        if (lastOrderItem?.status === "quote_provided") {
                            state.show_quote_buttons = true;
                        }
                    }

                    state.no_custimo_order = false;
                },
                onError: (error: any) => {
                    console.error("Error fetching order details:", error);
                    state.no_custimo_order = true;
                    showToast(
                        error.message || "Failed to fetch order details",
                        "error",
                    );
                },
            },
        );
    };

    // Measurement unit query
    const getMeasurementUnit = (orderId: string | number) => {
        return useVueQuery(
            `measurement-unit-${orderId}`,
            async () => {
                const url = `get-configuration/${orderId}/measurement_unit`;
                const response = await http.get(url);
                return response.data.result;
            },
            {
                enabled: !!orderId,
                staleTime: 10 * 60 * 1000, // 10 minutes
                cacheTime: 30 * 60 * 1000, // 30 minutes
                retry: 2,
                onError: (error: any) => {
                    console.error("Error fetching measurement unit:", error);
                    showToast("Failed to fetch measurement unit", "error");
                },
            },
        );
    };

    // Shipping companies query
    const getShippingCompanies = () => {
        return useVueQuery(
            "shipping-companies",
            async () => {
                const url = "get-shipping-companies";
                const response = await http.get(url);
                return response.data;
            },
            {
                staleTime: 30 * 60 * 1000, // 30 minutes
                cacheTime: 60 * 60 * 1000, // 1 hour
                retry: 2,
                onError: (error: any) => {
                    console.error("Error fetching shipping companies:", error);
                    showToast("Failed to fetch shipping companies", "error");
                },
            },
        );
    };

    // Quote methods
    const provideQuotePrice = (order: OrderDetail) => {
        quote.quote_order_id = order.id;
        quote.quote_order_no = order.order_no || "Quote pending";
        quote.quote_text = "";
        // Note: In the original component, this shows a modal
        // You might want to emit an event or use a different approach for modal management
        return quote;
    };

    const saveQuotePrice = async () => {
        if (quote.quote_text === "") {
            showToast("Please provide quote text.", "error");
            return false;
        }

        try {
            const response = await http.post("/save-quote-price", quote);
            const messageType = response.data.success ? "success" : "error";

            if (response.data.success) {
                // Reset quote data
                quote.quote_order_id = "";
                quote.quote_order_no = "";
                quote.quote_text = "";

                // Update state
                state.show_provide_quote_button = false;

                // Invalidate order detail to refresh data
                if (quote.quote_order_id) {
                    invalidateOrderDetail(quote.quote_order_id);
                }
            }

            showToast(response.data.message, messageType);
            return response.data.success;
        } catch (error: any) {
            showToast(
                error.response?.data?.message || "Failed to save quote",
                "error",
            );
            return false;
        }
    };

    const rejectQuote = async (order: OrderDetail) => {
        try {
            const payload = { order_id: order.id };
            const response = await http.post("reject-quote-order", payload);

            if (response.data.success) {
                showToast(response.data.message, "success");

                // Update state
                state.show_provide_quote_button = false;
                state.show_quote_buttons = false;

                // Invalidate order detail to refresh data
                invalidateOrderDetail(order.id);

                return true;
            } else {
                showToast(
                    "ERROR! Could not cancel the order, please try again.",
                    "error",
                );
                return false;
            }
        } catch (error: any) {
            showToast("Failed to reject quote", "error");
            return false;
        }
    };

    // Computed properties
    const isQuoteOrder = computed(() => state.is_quote_order);
    const showQuoteButtons = computed(() => state.show_quote_buttons);
    const showProvideQuoteButton = computed(
        () => state.show_provide_quote_button,
    );
    const orderFactories = computed(() => state.order_factories);
    const noCustimoOrder = computed(() => state.no_custimo_order);
    const quoteData = computed(() => quote);

    // Reset state
    const resetState = () => {
        state.is_quote_order = false;
        state.show_quote_buttons = false;
        state.show_provide_quote_button = false;
        state.no_custimo_order = false;
        state.order_factories = [];

        // Reset quote data
        quote.quote_text = "";
        quote.quote_order_id = "";
        quote.quote_order_no = "";
    };

    // Invalidate queries
    const invalidateOrderDetail = (orderId: string | number) => {
        queryClient.invalidateQueries({
            queryKey: [`order-detail-${orderId}`],
        });
    };

    const invalidateMeasurementUnit = (orderId: string | number) => {
        queryClient.invalidateQueries({
            queryKey: [`measurement-unit-${orderId}`],
        });
    };

    const invalidateShippingCompanies = () => {
        queryClient.invalidateQueries({ queryKey: ["shipping-companies"] });
    };

    const downloadImages = async (images: any, order_no: string) => {
        let urls = [];
        let imageNames = [];

        // Determine image names
        const timestamp = getCurrentDatetime();
        const baseName = order_no || timestamp;

        // Add front image
        urls.push({ url: images.frontImage });
        imageNames.push(`${baseName}-front-preview.png`);

        // Add back image only if it exists

        if (
            images.backImage &&
            images.backImage !== "null" &&
            images.backImage !== null
        ) {
            urls.push({ url: images.backImage });
            imageNames.push(`${baseName}-back-preview.png`);
        }

        try {
            const response = await http.post("convert-image-to-base64", {
                urls,
            });

            // Download each image with a small delay between them
            for (let i = 0; i < response.data.image_urls.length; i++) {
                const link = document.createElement("a");
                link.href = response.data.image_urls[i].url;
                link.download = imageNames[i];
                link.click();

                // Add a small delay between downloads
                if (i < response.data.image_urls.length - 1) {
                    await new Promise((resolve) => setTimeout(resolve, 500));
                }
            }

            showToast("Images downloaded successfully!", "success");
        } catch (error) {
            showToast("Error downloading images: " + error.message, "error");
            console.error("Download error:", error);
        }
    };

    const takeCommentAction = async (activity_comment) => {
        await http
            .put(`order_item/comment/${activity_comment.id}/take_action`, {
                action_taken: activity_comment.action_taken,
            })
            .then((successResponse) => {
                let response = successResponse.data;
                activity_comment.action_taken_by =
                    response.result.comment.action_taken_by;
                activity_comment.action_taken_at =
                    response.result.comment.action_taken_at;
            })
            .catch((errorResponse) => {
                activity_comment.action_taken = !activity_comment.action_taken;
                showToast(errorResponse, "error");
            });
    };

    return {
        // State
        isQuoteOrder,
        showQuoteButtons,
        showProvideQuoteButton,
        orderFactories,
        noCustimoOrder,
        quoteData,

        // Query methods
        getOrderDetail,
        getMeasurementUnit,
        getShippingCompanies,
        takeCommentAction,

        // Quote methods
        provideQuotePrice,
        saveQuotePrice,
        rejectQuote,

        // Utility methods
        resetState,
        invalidateOrderDetail,
        invalidateMeasurementUnit,
        invalidateShippingCompanies,
        downloadImages,

        // Constants from index file
        CustimooOrderFlowStatuses,
        activityStatus,
    };
};
